import FilterBlock from "@/api/query/filters/filter-block";
import Sort from "@/api/query/sorts/sort";
import Paging from "@/api/query/paging/paging";
import Logic from "@/api/query/filters/logic";
import SortGroup from "@/api/query/sorts/groups/sort-group";

/**
 * Построитель запросов для api
 */
export default class QueryBuilder {
    /**
     * @param {FilterBlock[]} filters
     * @param {Sort[]} sorts
     * @param {Paging[]} paging
     */
    constructor(filters = [], sorts = [], paging = null) {
        this.filters = [];
        this.sorts = [];
        this.paging = null;

        for (const filter of filters) {
            this.addFilter(filter);
        }

        for (const sort of sorts) {
            this.addSort(sort);
        }

        if (paging) {
            this.setPaging(paging);
        }
    }

    /**
     * @param {FilterBlock} filter
     */
    addFilter(filter) {
        if (!(filter instanceof FilterBlock)) {
            throw TypeError("filter must be instance of FilterBlock");
        }

        this.filters.push(filter);
    }

    /**
     * @param {Sort} sort
     */
    addSort(sort) {
        if (!(sort instanceof Sort) && !(sort instanceof SortGroup)) {
            throw TypeError("sort must be instance of Sort or SortGroup");
        }

        if (sort instanceof SortGroup) {
            this.sorts.push(...sort.getSorts());
        } else {
            this.sorts.push(sort);
        }
    }

    /**
     * @param {Paging} paging
     */
    setPaging(paging) {
        if (!(paging instanceof Paging)) {
            throw TypeError("paging must be instance of Paging");
        }

        this.paging = paging;
    }

    /**
     * Строит запрос, из блоков фильтров в строку
     * @returns {string}
     */
    build() {
        const query = {
            filters: this.getFiltersQueryChain(),
            sorts: this.getSortsQueryChain(),
            paging: this.getPagingQueryChain(),
        };

        return this.serialize(query);
    }

    /**
     * @returns {{}}
     */
    getSortsQueryChain() {
        let sortsQueryChain = {};

        for (const sort of this.sorts) {
            sortsQueryChain[sort.getField()] = sort.getOrder();
        }

        return sortsQueryChain;
    }

    /**
     * @returns {{}}
     */
    getFiltersQueryChain() {
        return this.makeFiltersQueryChain(this.filters);
    }

    /**
     * @returns {{perPage: number, page: number}}
     */
    getPagingQueryChain() {
        this.paging = this.paging || new Paging();

        return {
            page: this.paging.getPage(),
            per_page: this.paging.getPerPage(),
        };
    }

    /**
     * @param {Logic} filter
     * @returns {{}}
     */
    makeLogicFilterQueryChain(filter) {
        return {
            [filter.getLogic()]: this.makeFiltersQueryChain(filter.getFilters()),
        };
    }

    /**
     * @param {Filter} filter
     * @returns {{}}
     */
    makeFilterQueryChain(filter) {
        return {
            [filter.getField()]: {
                [filter.getOperator()]: filter.getValue(),
            },
        };
    }

    /**
     * @param {FilterBlock[]|Filter[]} filters
     * @returns {{}}
     */
    makeFiltersQueryChain(filters) {
        let queryChain = {};
        let index = 0;

        for (let subFilter of filters) {
            if (subFilter instanceof Logic) {
                queryChain[index] = this.makeLogicFilterQueryChain(subFilter);
            } else {
                this.objCombine(this.makeFilterQueryChain(subFilter), queryChain);
            }

            index++;
        }

        return queryChain;
    }

    objCombine(object, variable) {
        for (const key of Object.keys(object)) {
            if (!variable[key]) {
                variable[key] = {};
            }

            for (const innerKey of Object.keys(object[key])) {
                variable[key][innerKey] = object[key][innerKey];
            }
        }
    }

    /**
     * @param object
     * @param prefix
     * @returns {string}
     */
    serialize(object, prefix) {
        let string = [];

        for (let objectProperty in object) {
            if (object.propertyIsEnumerable.call(object, objectProperty)) {
                let key = prefix ? prefix + "[" + objectProperty + "]" : objectProperty;
                let value = object[objectProperty];

                value =
                    value !== null && typeof value === "object"
                        ? this.serialize(value, key)
                        : encodeURIComponent(key) + "=" + encodeURIComponent(value);

                string.push(value);
            }
        }

        return string.join("&");
    }
}
